import React from "react";
import { Accordion, Badge, Button, Group, Paper, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconCar } from "@tabler/icons-react";
import type { DriverRequestDto, Language } from "../types";
import type { Texts } from "../texts";

type Props = {
    requests: DriverRequestDto[];
    language: Language;
    onAddDriverLog: (requestId: number, statusId: number, note?: string) => void;
    t: Texts;
};

function getDriverStatusColor(statusId?: number) {
    switch (statusId) {
        case 1:
            return "blue";
        case 2:
            return "yellow";
        case 3:
            return "orange";
        case 4:
            return "green";
        default:
            return "gray";
    }
}

function getDriverStatusLabel(statusId: number | undefined, t: Texts) {
    switch (statusId) {
        case 1:
            return t.onTheWay;
        case 2:
            return t.atPickup;
        case 3:
            return t.pickedUp;
        case 4:
            return t.delivered;
        default:
            return t.planned;
    }
}

export default function DriverRidesSection({
    requests,
    onAddDriverLog,
    t,
}: Props) {
    if (requests.length === 0) return null;

    return (
        <Accordion.Item value="driver-rides">
            <Accordion.Control
                icon={
                    <ThemeIcon color="red" variant="light" radius="xl">
                        <IconCar size={18} />
                    </ThemeIcon>
                }
            >
                {t.driverRides}
            </Accordion.Control>

            <Accordion.Panel>
                <Stack gap="md">
                    {requests.map((r) => {
                        const pickupText =
                            r.pickUpPlace && r.pickUpPlace.trim()
                                ? r.pickUpPlace
                                : r.kind === 1
                                    ? t.airport
                                    : "-";

                        const dropOffText =
                            r.dropOffPlace && r.dropOffPlace.trim()
                                ? r.dropOffPlace
                                : r.kind === 2
                                    ? t.airport
                                    : "-";

                        return (
                            <Paper key={r.transportRequestId} withBorder p="md" radius="md">
                                <Stack gap="sm">
                                    <Group justify="space-between" align="flex-start">
                                        <div>
                                            <Text fw={700}>
                                                {r.contactPerson || t.unknownContact}
                                            </Text>

                                            <Text size="sm" c="dimmed">
                                                {r.scheduledDate || ""} {r.scheduledTime || ""}
                                            </Text>
                                        </div>

                                        <Badge
                                            color={getDriverStatusColor(r.currentDriverStatusId)}
                                            variant="light"
                                        >
                                            {getDriverStatusLabel(r.currentDriverStatusId, t)}
                                        </Badge>
                                    </Group>

                                    <Text size="sm">
                                        <strong>{t.from}:</strong> {pickupText}
                                    </Text>

                                    <Text size="sm">
                                        <strong>{t.to}:</strong> {dropOffText}
                                    </Text>

                                    {r.flightNo && (
                                        <Text size="sm">
                                            <strong>{t.flight}:</strong> {r.flightNo}
                                        </Text>
                                    )}

                                    {(r.vehicleName || r.vehiclePlate) && (
                                        <Text size="sm">
                                            <strong>{t.vehicle}:</strong> {r.vehicleName || "-"}
                                            {r.vehiclePlate ? ` (${r.vehiclePlate})` : ""}
                                        </Text>
                                    )}

                                    {r.note && (
                                        <Text size="sm">
                                            <strong>{t.note}:</strong> {r.note}
                                        </Text>
                                    )}

                                    <Group gap="xs" mt="xs">
                                        <Button
                                            size="xs"
                                            variant="light"
                                            onClick={() => onAddDriverLog(r.transportRequestId, 1)}
                                        >
                                            {t.onTheWay}
                                        </Button>

                                        <Button
                                            size="xs"
                                            variant="light"
                                            color="yellow"
                                            onClick={() => onAddDriverLog(r.transportRequestId, 2)}
                                        >
                                            {t.atPickup}
                                        </Button>

                                        <Button
                                            size="xs"
                                            variant="light"
                                            color="orange"
                                            onClick={() => onAddDriverLog(r.transportRequestId, 3)}
                                        >
                                            {t.pickedUp}
                                        </Button>

                                        <Button
                                            size="xs"
                                            variant="light"
                                            color="green"
                                            onClick={() => onAddDriverLog(r.transportRequestId, 4)}
                                        >
                                            {t.delivered}
                                        </Button>
                                    </Group>
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            </Accordion.Panel>
        </Accordion.Item>
    );
}